import { Pencil, Plus, Power, RefreshCw, Upload, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type {
  CreateVendorRequest,
  ImportVendorsResponse,
  UpdateVendorRequest,
  Vendor,
  VendorCsvRow,
  VendorsListResponse,
} from '@/types'
import { EmptyState, Spinner } from '@/components/ui'
import { RequestError, api } from '@/lib/api'
import { AdminSection } from './AdminSection'

const CSV_COLUMNS = ['nombre', 'nro_vendedor', 'email', 'telefono', 'empresa', 'sucursal', 'otros', 'otros1'] as const

/* ---------- helpers de UI ---------- */
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet no-scrollbar overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="flex items-center justify-between px-4 py-2">
          <h3 className="font-bold text-ink">{title}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 text-ink-muted hover:bg-surface-sunken" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-4 pb-8">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
    </div>
  )
}

/* ---------- formulario alta/edición ---------- */
type TextKey = 'fullName' | 'vendorNumber' | 'email' | 'phone' | 'company' | 'branch' | 'extra' | 'extra1' | 'password'
interface FormState {
  id?: string
  fullName: string
  vendorNumber: string
  email: string
  phone: string
  company: string
  branch: string
  extra: string
  extra1: string
  isAdmin: boolean
  password: string
}
const EMPTY_FORM: FormState = {
  fullName: '', vendorNumber: '', email: '', phone: '', company: '', branch: '', extra: '', extra1: '', isAdmin: false, password: '',
}
const vendorToForm = (v: Vendor): FormState => ({
  id: v.id, fullName: v.fullName, vendorNumber: v.vendorNumber, email: v.email, phone: v.phone ?? '',
  company: v.company, branch: v.branch, extra: v.extra ?? '', extra1: v.extra1 ?? '', isAdmin: v.isAdmin, password: '',
})

function VendorForm({
  initial,
  original,
  onClose,
  onSaved,
}: {
  initial: FormState
  original?: Vendor
  onClose: () => void
  onSaved: (vendor: Vendor, isEdit: boolean) => void
}) {
  const isEdit = !!initial.id
  const [f, setF] = useState<FormState>(initial)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const set = (k: TextKey) => (e: React.ChangeEvent<HTMLInputElement>) => setF((p) => ({ ...p, [k]: e.target.value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      if (isEdit) {
        const body: UpdateVendorRequest = {
          fullName: f.fullName, vendorNumber: f.vendorNumber, email: f.email, phone: f.phone,
          company: f.company, branch: f.branch, extra: f.extra, extra1: f.extra1, isAdmin: f.isAdmin,
        }
        if (f.password.trim()) body.password = f.password.trim()
        await api.patch(`/vendors/${f.id}`, body)
        const merged: Vendor = {
          ...(original as Vendor),
          fullName: f.fullName, vendorNumber: f.vendorNumber, email: f.email, phone: f.phone || undefined,
          company: f.company, branch: f.branch, extra: f.extra || undefined, extra1: f.extra1 || undefined, isAdmin: f.isAdmin,
        }
        onSaved(merged, true)
      } else {
        const body: CreateVendorRequest = {
          fullName: f.fullName, vendorNumber: f.vendorNumber, email: f.email, phone: f.phone || undefined,
          company: f.company, branch: f.branch, extra: f.extra || undefined, extra1: f.extra1 || undefined,
          isAdmin: f.isAdmin, password: f.password,
        }
        const created = await api.post<Vendor>('/vendors', body)
        onSaved(created, false)
      }
    } catch (e2) {
      setErr(e2 instanceof RequestError ? e2.message : 'No se pudo guardar.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={isEdit ? 'Editar vendedor' : 'Nuevo vendedor'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Nombre completo *"><input className="field-input" required value={f.fullName} onChange={set('fullName')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nº vendedor *"><input className="field-input" required value={f.vendorNumber} onChange={set('vendorNumber')} /></Field>
          <Field label="Sucursal *"><input className="field-input" required value={f.branch} onChange={set('branch')} /></Field>
        </div>
        <Field label="Email *"><input type="email" className="field-input" required value={f.email} onChange={set('email')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono"><input className="field-input" value={f.phone} onChange={set('phone')} /></Field>
          <Field label="Empresa *"><input className="field-input" required value={f.company} onChange={set('company')} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Otros"><input className="field-input" value={f.extra} onChange={set('extra')} /></Field>
          <Field label="Otros 1"><input className="field-input" value={f.extra1} onChange={set('extra1')} /></Field>
        </div>
        <Field label={isEdit ? 'Nueva contraseña (opcional)' : 'Contraseña inicial *'}>
          <input type="text" className="field-input" required={!isEdit} value={f.password} onChange={set('password')} placeholder="mín. 8 caracteres, 1 número" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input type="checkbox" checked={f.isAdmin} onChange={(e) => setF((p) => ({ ...p, isAdmin: e.target.checked }))} />
          Es administrador
        </label>
        {err && <div className="rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>}
        <button className="btn-primary btn-block" disabled={busy}>{busy ? <Spinner className="h-4 w-4" /> : 'Guardar'}</button>
      </form>
    </Modal>
  )
}

/* ---------- import CSV ---------- */
function parseCsv(text: string): VendorCsvRow[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
  if (!lines.length) return []
  const start = /email|nombre|nro/i.test(lines[0]) ? 1 : 0 // salta header si lo hay
  return lines.slice(start).map((line) => {
    const c = line.split(',').map((s) => s.trim())
    return {
      nombre: c[0] ?? '', nro_vendedor: c[1] ?? '', email: c[2] ?? '', telefono: c[3] ?? '',
      empresa: c[4] ?? '', sucursal: c[5] ?? '', otros: c[6] ?? '', otros1: c[7] ?? '',
    }
  })
}

function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [rows, setRows] = useState<VendorCsvRow[]>([])
  const [result, setResult] = useState<ImportVendorsResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setRows(parseCsv(String(reader.result || '')))
      setResult(null)
      setErr(null)
    }
    reader.readAsText(file)
  }

  function downloadTemplate() {
    const csv = `${CSV_COLUMNS.join(',')}\nJuan Pérez,1001,juan@empresa.com,1155550000,Mi Empresa,Central,,`
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'plantilla-vendedores.csv'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function confirm() {
    setBusy(true)
    setErr(null)
    try {
      setResult(await api.post<ImportVendorsResponse>('/vendors/import', { rows }))
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Importar vendedores (CSV)" onClose={() => { if (result) onDone(); onClose() }}>
      {result ? (
        <div className="space-y-3">
          <p className="text-sm text-ink">Importados: <b>{result.created.length}</b> · Omitidos: <b>{result.skipped.length}</b></p>
          {result.created.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold text-ink-soft">Contraseñas temporales (comunicáselas a cada vendedor):</p>
              <div className="card divide-y divide-line text-xs">
                {result.created.map((c) => (
                  <div key={c.email} className="flex justify-between gap-2 px-3 py-1.5">
                    <span className="truncate">{c.email}</span>
                    <code className="accent-text">{c.tempPassword}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.skipped.length > 0 && (
            <div className="text-xs text-ink-muted">
              <p className="mb-1 font-semibold">Omitidos:</p>
              {result.skipped.map((s, i) => <p key={i}>{s.email} — {s.reason}</p>)}
            </div>
          )}
          <button className="btn-primary btn-block" onClick={() => { onDone(); onClose() }}>Listo</button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">
            Columnas en orden: <code className="text-[11px]">{CSV_COLUMNS.join(', ')}</code>. Email y nº únicos; los duplicados se omiten.
          </p>
          <button className="btn-secondary btn-block" onClick={downloadTemplate}>Descargar plantilla</button>
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" />
          {rows.length > 0 && (
            <div>
              <p className="mb-1 text-xs text-ink-muted">Vista previa ({rows.length} filas; primeras 5):</p>
              <div className="card divide-y divide-line text-xs">
                {rows.slice(0, 5).map((r, i) => (
                  <div key={i} className="px-3 py-1.5"><b>{r.nombre}</b> · {r.nro_vendedor} · {r.email}</div>
                ))}
              </div>
            </div>
          )}
          {err && <div className="rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>}
          <button className="btn-primary btn-block" onClick={confirm} disabled={busy || rows.length === 0}>
            {busy ? <Spinner className="h-4 w-4" /> : `Confirmar importación (${rows.length})`}
          </button>
        </div>
      )}
    </Modal>
  )
}

/* ---------- pantalla ---------- */
export function AdminVendors() {
  const [vendors, setVendors] = useState<Vendor[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [form, setForm] = useState<FormState | null>(null)
  const [original, setOriginal] = useState<Vendor | null>(null)
  const [importing, setImporting] = useState(false)

  async function load() {
    setErr(null)
    try {
      setVendors((await api.get<VendorsListResponse>('/vendors')).vendors)
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  useEffect(() => {
    void load()
  }, [])

  // Optimistic: MD tiene read-after-write lag, así que reflejamos el cambio local
  // sin depender del reload inmediato.
  function handleSaved(vendor: Vendor, isEdit: boolean) {
    setForm(null)
    setOriginal(null)
    setVendors((list) => {
      const cur = list ?? []
      return isEdit ? cur.map((v) => (v.id === vendor.id ? vendor : v)) : [vendor, ...cur]
    })
  }

  async function toggleActive(v: Vendor) {
    const snapshot = vendors
    setVendors((list) => list?.map((x) => (x.id === v.id ? { ...x, active: !x.active } : x)) ?? null)
    try {
      await api.patch(`/vendors/${v.id}`, { active: !v.active })
    } catch (e) {
      setVendors(snapshot ?? null)
      setErr((e as Error).message)
    }
  }

  function openCreate() {
    setOriginal(null)
    setForm({ ...EMPTY_FORM })
  }
  function openEdit(v: Vendor) {
    setOriginal(v)
    setForm(vendorToForm(v))
  }

  return (
    <AdminSection title="Vendedores">
      <div className="mb-3 flex gap-2">
        <button className="btn-primary flex-1" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Agregar
        </button>
        <button className="btn-secondary flex-1" onClick={() => setImporting(true)}>
          <Upload className="h-4 w-4" /> Importar CSV
        </button>
      </div>

      {err && <div className="mb-3 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>}

      <button className="mb-2 ml-auto flex items-center gap-1 text-xs font-medium accent-text" onClick={() => void load()}>
        <RefreshCw className="h-3 w-3" /> Actualizar lista
      </button>

      {vendors === null ? (
        <div className="flex justify-center py-10"><Spinner className="h-6 w-6 accent-text" /></div>
      ) : vendors.length === 0 ? (
        <EmptyState title="Sin vendedores" subtitle="Agregá el primero o importá un CSV." />
      ) : (
        <div className="space-y-2">
          {vendors.map((v) => (
            <div key={v.id} className="card flex items-center gap-2 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">
                  {v.fullName} {v.isAdmin && <span className="badge ml-1">admin</span>}
                </p>
                <p className="text-xs text-ink-muted">Nº {v.vendorNumber} · {v.branch}</p>
              </div>
              <span className={v.active ? 'text-[11px] font-semibold text-success' : 'text-[11px] font-semibold text-ink-faint'}>
                {v.active ? 'Activo' : 'Inactivo'}
              </span>
              <button className="btn-ghost px-2 py-1" onClick={() => openEdit(v)} aria-label="Editar">
                <Pencil className="h-4 w-4" />
              </button>
              <button className="btn-ghost px-2 py-1" onClick={() => toggleActive(v)} aria-label="Activar/desactivar">
                <Power className={v.active ? 'h-4 w-4 text-danger' : 'h-4 w-4 text-success'} />
              </button>
            </div>
          ))}
        </div>
      )}

      {form && (
        <VendorForm
          initial={form}
          original={original ?? undefined}
          onClose={() => { setForm(null); setOriginal(null) }}
          onSaved={handleSaved}
        />
      )}
      {importing && <ImportModal onClose={() => setImporting(false)} onDone={load} />}
    </AdminSection>
  )
}
